/**
 * Date to puzzle mapping, and the weekly difficulty curve.
 *
 * Puzzle number 1 is 2026-01-01 local. Difficulty depends on the weekday
 * only, so the shape of everyone's week is the same: gentle on Monday,
 * savage on Sunday, like a Saturday crossword.
 */

/** Bump to regenerate the whole calendar. Puzzles are frozen per version. */
export const GEN_VERSION = 1;

const EPOCH = { year: 2026, month: 0, day: 1 };

export interface Spec {
  /** How many pigments are on the bench. */
  pigments: number;
  /** Blend budget. The target is always reachable in exactly this many. */
  blends: number;
  /** Win threshold, in scaled OKLab distance units. */
  tolerance: number;
  /** Shown on the home screen. */
  label: string;
  /** Shown under the label. */
  blurb: string;
}

/** Indexed by weekday, 0 = Sunday. Weekends bite. */
const WEEK: readonly Spec[] = [
  { pigments: 7, blends: 7, tolerance: 5.0, label: 'Master', blurb: 'Sunday. No mercy.' },
  { pigments: 6, blends: 4, tolerance: 8.5, label: 'Gentle', blurb: 'A soft start to the week.' },
  { pigments: 6, blends: 5, tolerance: 7.5, label: 'Easy', blurb: 'Room to experiment.' },
  { pigments: 6, blends: 5, tolerance: 6.8, label: 'Steady', blurb: 'Midweek, mid-weight.' },
  { pigments: 7, blends: 6, tolerance: 6.0, label: 'Sharp', blurb: 'Needs a plan.' },
  { pigments: 7, blends: 6, tolerance: 5.5, label: 'Hard', blurb: 'Friday, and it knows it.' },
  { pigments: 7, blends: 7, tolerance: 4.8, label: 'Brutal', blurb: 'Saturday. Bring an eye.' },
];

/** Whole days from local epoch midnight. Stable across DST. */
export function dayIndexFor(date: Date): number {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const epoch = new Date(EPOCH.year, EPOCH.month, EPOCH.day);
  return Math.round((local.getTime() - epoch.getTime()) / 86400000);
}

export function dateForDayIndex(index: number): Date {
  const d = new Date(EPOCH.year, EPOCH.month, EPOCH.day);
  d.setDate(d.getDate() + index);
  return d;
}

export function todayIndex(now: Date = new Date()): number {
  return dayIndexFor(now);
}

/** Human-facing puzzle number. Day 0 is puzzle #1. */
export function puzzleNumber(index: number): number {
  return index + 1;
}

export function specForDay(index: number): Spec {
  return WEEK[dateForDayIndex(index).getDay()];
}

export function isoSuffix(index: number): string {
  return `#${String(puzzleNumber(index)).padStart(3, '0')}`;
}

/** Milliseconds until the next puzzle unlocks, in local time. */
export function msUntilNextPuzzle(now: Date = new Date()): number {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return next.getTime() - now.getTime();
}

export function longDate(index: number): string {
  return dateForDayIndex(index).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}
